import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI, locationsAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Plus, Search, Warehouse, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

export default function Inventory() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [adjustment, setAdjustment] = useState({
    reason: 'cycle_count',
    quantity_change: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invRes, itemRes, locRes] = await Promise.all([
        inventoryAPI.list(),
        itemsAPI.listAll(),
        locationsAPI.list()
      ]);
      setInventory(invRes.data);
      setItems(itemRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemDetails = (itemId) => items.find(i => i.id === itemId);
  const getLocationName = (locationId) => locations.find(l => l.id === locationId)?.name || '-';

  const enrichedInventory = inventory.map(inv => {
    const item = getItemDetails(inv.item_id);
    // Get min_stock from variant level, falling back to item level
    const variant = item?.variants?.find(v => v.sku === inv.variant_sku);
    const min_stock = variant?.min_stock > 0 ? variant.min_stock : (item?.min_stock || 0);
    return {
      ...inv,
      item_name: item?.name || 'Unknown',
      category: item?.category || '-',
      min_stock,
      is_low_stock: min_stock > 0 && inv.quantity <= min_stock,
      location_name: getLocationName(inv.location_id)
    };
  });

  const filteredInventory = enrichedInventory.filter(inv => {
    const matchesSearch = 
      inv.item_name.toLowerCase().includes(search.toLowerCase()) ||
      inv.variant_sku.toLowerCase().includes(search.toLowerCase());
    const matchesLocation = locationFilter === 'all' || inv.location_id === locationFilter;
    const matchesLowStock = !showLowStock || inv.is_low_stock;
    return matchesSearch && matchesLocation && matchesLowStock;
  });

  const handleAdjust = async () => {
    if (!selectedInv || adjustment.quantity_change === 0) {
      toast.error('Please enter a valid adjustment');
      return;
    }

    try {
      await inventoryAPI.adjust(selectedInv.id, adjustment);
      toast.success('Inventory adjusted');
      setShowAdjust(false);
      setSelectedInv(null);
      setAdjustment({ reason: 'cycle_count', quantity_change: 0, notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Uniform Inventory</h1>
          <p className="text-slate-500 mt-1">
            Track stock levels across all warehouses
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total SKUs</p>
            <p className="text-2xl font-bold">{inventory.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Low Stock Items</p>
            <p className="text-2xl font-bold text-amber-600">
              {enrichedInventory.filter(i => i.is_low_stock).length}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Units</p>
            <p className="text-2xl font-bold">
              {inventory.reduce((sum, i) => sum + i.quantity, 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Value</p>
            <p className="text-2xl font-bold">
              {formatCurrency(enrichedInventory.reduce((sum, inv) => {
                const item = getItemDetails(inv.item_id);
                const variant = item?.variants?.find(v => v.sku === inv.variant_sku);
                const unitPrice = variant?.price || 0;
                return sum + (inv.quantity * unitPrice);
              }, 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by item or SKU..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="inventory-search"
              />
            </div>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="location-filter">
                <SelectValue placeholder="Warehouse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant={showLowStock ? 'default' : 'outline'}
              onClick={() => setShowLowStock(!showLowStock)}
              data-testid="low-stock-filter"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Stock Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 skeleton-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">On Hand</TableHead>
                <TableHead className="text-right">Reserved</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">On Order</TableHead>
                <TableHead>Status</TableHead>
                <RequireCap module="assets" action="edit"><TableHead></TableHead></RequireCap>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInventory.map(inv => (
                <TableRow key={inv.id} data-testid={`inventory-row-${inv.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{inv.item_name}</p>
                      <p className="text-xs text-slate-500">{inv.category}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{inv.variant_sku}</TableCell>
                  <TableCell>{inv.location_name}</TableCell>
                  <TableCell className="text-right font-medium">{inv.quantity}</TableCell>
                  <TableCell className="text-right">{inv.reserved}</TableCell>
                  <TableCell className="text-right font-medium text-blue-600">{inv.available}</TableCell>
                  <TableCell className="text-right">{inv.on_order}</TableCell>
                  <TableCell>
                    {inv.is_low_stock ? (
                      <Badge variant="destructive" className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Low Stock
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-emerald-600">In Stock</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <RequireCap module="assets" action="edit">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedInv(inv);
                          setShowAdjust(true);
                        }}
                        data-testid={`adjust-${inv.id}`}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                      </Button>
                    </RequireCap>
                  </TableCell>
                </TableRow>
              ))}
              {filteredInventory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                    <Warehouse className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No inventory records found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Adjustment Dialog */}
      <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
            <DialogDescription>
              {selectedInv && `${selectedInv.item_name} - ${selectedInv.variant_sku}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Current Quantity</p>
              <p className="text-2xl font-bold">{selectedInv?.quantity || 0}</p>
            </div>
            <div className="space-y-2">
              <Label>Quantity Change (+ or -)</Label>
              <Input
                type="number"
                value={adjustment.quantity_change}
                onChange={(e) => setAdjustment({ ...adjustment, quantity_change: parseInt(e.target.value) || 0 })}
                placeholder="Enter + to add, - to remove"
                data-testid="adjustment-quantity"
              />
              <p className="text-xs text-slate-500">
                New quantity will be: {(selectedInv?.quantity || 0) + adjustment.quantity_change}
              </p>
            </div>
            <Button onClick={handleAdjust} className="w-full" data-testid="save-adjustment">
              Apply Adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
